import { formatDealValue } from "./formatDealValue";

/**
 * Offer economics for IOI/LOI terms on the GTM buyer tracker.
 *
 * The multiple is the canonical number: EV is re-derived from
 * multiple × current client EBITDA wherever it's displayed, so updating
 * EBITDA moves EV and the multiple holds. The EV typed at entry is kept
 * as an audit snapshot and as the display fallback when EBITDA is unset.
 */

export interface OfferTerms {
  multipleLow: number;
  multipleHigh: number;
  evLowEntered: number;
  evHighEntered: number;
  ebitdaAtEntry: number;
  rolloverPct: number | null;
  earnoutAmount: number | null;
  earnoutInEv: boolean | null;
  sellerFinancingAmount: number | null;
  notes: string | null;
}

export interface EvPoints { low: number; mid: number; high: number; derived: boolean }

export function computeMultiples(evLow: number, evHigh: number, ebitda: number): { low: number; high: number } {
  return { low: evLow / ebitda, high: evHigh / ebitda };
}

export function deriveEv(
  t: Pick<OfferTerms, "multipleLow" | "multipleHigh" | "evLowEntered" | "evHighEntered">,
  currentEbitda: number | null,
): EvPoints {
  const derived = currentEbitda != null && currentEbitda > 0;
  const low = derived ? t.multipleLow * currentEbitda : t.evLowEntered;
  const high = derived ? t.multipleHigh * currentEbitda : t.evHighEntered;
  return { low, mid: (low + high) / 2, high, derived };
}

export function rolloverDollars(pct: number | null, ev: number): number {
  return pct != null ? (pct / 100) * ev : 0;
}

export function cashAtClosing(t: OfferTerms, ev: number): number {
  return ev
    - rolloverDollars(t.rolloverPct, ev)
    - (t.sellerFinancingAmount ?? 0)
    - (t.earnoutInEv ? (t.earnoutAmount ?? 0) : 0);
}

/** EV + earnout — only meaningful when the earnout sits on top of EV. */
export function totalConsideration(t: OfferTerms, ev: number): number | null {
  if (t.earnoutAmount == null || t.earnoutAmount <= 0 || t.earnoutInEv !== false) return null;
  return ev + t.earnoutAmount;
}

export function formatMultiple(m: number | null): string {
  return m != null && Number.isFinite(m) ? `${m.toFixed(1)}x` : "—";
}

export function formatMultipleRange(low: number | null, high: number | null): string {
  if (low == null || high == null) return formatMultiple(low ?? high);
  const l = low.toFixed(1), h = high.toFixed(1);
  return l === h ? `${l}x` : `${l}–${h}x`;
}

/** "$8M–$12M", collapsing single-value bids to "$8M". */
export function formatEvRange(low: number, high: number): string {
  const l = formatDealValue(String(Math.round(low)));
  const h = formatDealValue(String(Math.round(high)));
  return l === h ? l : `${l}–${h}`;
}

/** One row of GET /api/companies/:id/offers (OfferRow in client/src/hooks/useBuyerTracker.ts). */
export interface OfferRowLike {
  linkId: string; buyerId: string; buyerName: string;
  status: string | null; receivedAt: string | null; terms: OfferTerms;
}

/** A bid as the Client page shows it. */
export interface OfferLine {
  linkId: string; buyerId: string; buyerName: string;
  stage: "IOI" | "LOI";
  multiple: string; // "5.5–6.0x"
  ev: string; // "$22M–$24M", from the multiple and today's EBITDA when it is set
  evDerived: boolean; // false: EBITDA is unset, so this is the EV typed at entry
  midMultiple: number;
  receivedOn: string | null; // YYYY-MM-DD, the stored day
  passed: boolean;
}

/**
 * One line per buyer at the furthest stage they bid: their LOI when they sent
 * one, else their IOI. LOIs first, then the highest mid multiple. The multiple
 * is canonical and EV follows today's EBITDA, as everywhere offers are shown.
 */
export function offerLines(offers: { ebitda: number | null; ioi: OfferRowLike[]; loi: OfferRowLike[] } | null | undefined): OfferLine[] {
  if (!offers) return [];
  const toLine = (stage: "IOI" | "LOI") => (r: OfferRowLike): OfferLine => {
    const ev = deriveEv(r.terms, offers.ebitda);
    return {
      linkId: r.linkId, buyerId: r.buyerId, buyerName: r.buyerName, stage,
      multiple: formatMultipleRange(r.terms.multipleLow, r.terms.multipleHigh),
      ev: formatEvRange(ev.low, ev.high),
      evDerived: ev.derived,
      midMultiple: (r.terms.multipleLow + r.terms.multipleHigh) / 2,
      receivedOn: r.receivedAt && /^\d{4}-\d{2}-\d{2}/.test(r.receivedAt) ? r.receivedAt.slice(0, 10) : null,
      passed: r.status === "passed",
    };
  };
  const byMultiple = (a: OfferLine, b: OfferLine) => b.midMultiple - a.midMultiple;
  const lois = offers.loi.map(toLine("LOI")).sort(byMultiple);
  const withLoi = new Set(lois.map((l) => l.buyerId));
  const iois = offers.ioi.filter((r) => !withLoi.has(r.buyerId)).map(toLine("IOI")).sort(byMultiple);
  return [...lois, ...iois];
}

/** The best live offer: the highest-multiple LOI, else the highest IOI, from a buyer who has not passed. */
export function bestOffer(lines: OfferLine[]): OfferLine | null {
  return lines.find((l) => !l.passed && l.stage === "LOI") ?? lines.find((l) => !l.passed) ?? null;
}
